/**
 * StorybookReadinessPanel — Legacy Book readiness display for AI Story Studio.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { getApiBase } from '../../utils/apiBase';
import { treeHref } from '../../utils/prettyArchiveLinks';
import { STORY_STUDIO_SAFETY } from '../../content/storyStudioContent';

const API = getApiBase();

const METRICS = [
  { key: 'prompts_answered', label: 'Completed prompts' },
  { key: 'prompts_drafted', label: 'Draft answers' },
  { key: 'chapters_started', label: 'Chapters begun', suffix: (r) => ` of ${r.chapters_total}` },
  { key: 'voice_memories', label: 'Voice memories' },
  { key: 'stories_in_archive', label: 'Stories ready for export' },
  { key: 'photos_documents_attached', label: 'Photos & documents' },
];

export function StorybookReadinessPanel({ readiness, progress, treeId, className = '' }) {
  const [previewLoading, setPreviewLoading] = useState(false);

  if (!readiness) return null;

  const pct = readiness.book_progress_percent ?? progress?.percent_complete ?? 0;

  const handlePreviewLegacyBook = async () => {
    if (!treeId) return;
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API}/trees/${treeId}/export/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success('Opening Legacy Book PDF — use Review draft in archive settings for readiness notes.');
    } catch {
      toast.error('Could not generate preview yet. Complete a few answers first.');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <section
      className={`rounded-2xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_99%)] px-5 py-4 ${className}`}
      data-testid="storybook-readiness-panel"
      aria-label="Legacy Book readiness"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
            Legacy Book readiness
          </p>
          <p className="mt-0.5 font-serif text-sm text-foreground/85">
            Completed stories can be reviewed and included in your Legacy Book PDF.
          </p>
        </div>
        <span className="shrink-0 font-serif text-2xl font-light tabular-nums text-[#2C4A3B]">
          {pct}%
        </span>
      </div>

      <div className="mb-4 h-1 overflow-hidden rounded-full bg-[hsl(38_14%_88%)]/50">
        <div
          className="h-full rounded-full bg-[#2C4A3B]/90 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="space-y-2">
        {METRICS.map(({ key, label, suffix }) => {
          const val = readiness[key];
          if (val == null && key === 'prompts_drafted') return null;
          const display = suffix ? `${val ?? 0}${suffix(readiness)}` : (val ?? 0);
          return (
            <li key={key} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground/80">{label}</span>
              <span className="tabular-nums text-foreground/75">{display}</span>
            </li>
          );
        })}
      </ul>

      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground/70">{STORY_STUDIO_SAFETY}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {treeId ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full text-xs"
            disabled={previewLoading}
            onClick={handlePreviewLegacyBook}
            data-testid="storybook-preview-legacy-book"
          >
            {previewLoading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                Generating…
              </>
            ) : (
              <>
                <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Preview Legacy Book
              </>
            )}
          </Button>
        ) : null}
        {treeId ? (
          <Button size="sm" variant="ghost" className="rounded-full text-xs" asChild>
            <Link to={`${treeHref({ id: treeId })}?tab=readiness`}>Archive readiness</Link>
          </Button>
        ) : null}
      </div>

      {readiness.ready_for_preview ? (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-[#2C4A3B]/80">
          <Check className="h-3.5 w-3.5" aria-hidden />
          Enough material gathered to begin previewing your Legacy Book.
        </p>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground/60">
          A few more answers and you&apos;ll be ready to preview your keepsake book.
        </p>
      )}
    </section>
  );
}

export default StorybookReadinessPanel;
